import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedNumber — counts up to numeric portion when in view.
 * Accepts strings like "150+", "+124%", "3.8×".
 * Renders the static value until first scroll-into-view, then counts up.
 */
const parseValue = (raw) => {
  const match = String(raw).match(/^([^\d-]*)(-?\d+(?:[.,]\d+)?)(.*)$/);
  if (!match) return { prefix: "", num: null, suffix: String(raw) };
  return {
    prefix: match[1],
    num: parseFloat(match[2].replace(",", ".")),
    suffix: match[3],
  };
};

const AnimatedNumber = ({ value, duration = 1500, className = "" }) => {
  const ref = useRef(null);
  const [played, setPlayed] = useState(false);
  const [display, setDisplay] = useState(null);
  const { prefix, num, suffix } = parseValue(value);

  useEffect(() => {
    if (!ref.current || num === null) return;
    const el = ref.current;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !played) {
            setPlayed(true);
            setDisplay(0);
            const start = performance.now();
            const decimals = String(num).includes(".") ? 1 : 0;
            const tick = (now) => {
              const t = Math.min(1, (now - start) / duration);
              const eased = 1 - Math.pow(1 - t, 3);
              setDisplay(Number((num * eased).toFixed(decimals)));
              if (t < 1) requestAnimationFrame(tick);
            };
            requestAnimationFrame(tick);
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [num, duration, played]);

  // Static value until first trigger — avoids '0' flash before IntersectionObserver fires
  if (num === null || display === null) {
    return <span ref={ref} className={className}>{value}</span>;
  }

  return (
    <span ref={ref} className={className}>
      {prefix}
      {display}
      {suffix}
    </span>
  );
};

export default AnimatedNumber;
